<template>
  <div class="min-h-screen hero-gradient">
    <!-- Navigation -->
    <nav class="relative z-50 px-6 py-4">
      <div class="max-w-7xl mx-auto flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <div class="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
            <QrCodeIcon class="w-6 h-6 text-white" />
          </div>
          <span class="text-xl font-bold text-gradient">Digital Menu AI</span>
        </div>
        <div class="hidden md:flex items-center space-x-8">
          <a href="#features" class="text-gray-300 hover:text-white transition-colors">Features</a>
          <a href="#pricing" class="text-gray-300 hover:text-white transition-colors">Pricing</a>
          <a href="#contact" class="text-gray-300 hover:text-white transition-colors">Contact</a>
          <router-link to="/login" class="text-gray-300 hover:text-white transition-colors">Login</router-link>
          <router-link to="/register" class="btn-primary">Get Started</router-link>
        </div>
        <button @click="toggleMobileMenu" class="md:hidden text-white">
          <MenuIcon class="w-6 h-6" />
        </button>
      </div>
      
      <!-- Mobile Menu -->
      <div v-if="showMobileMenu" class="md:hidden absolute top-full left-0 right-0 bg-dark-800/95 backdrop-blur-lg border-t border-white/10">
        <div class="px-6 py-4 space-y-4">
          <a href="#features" class="block text-gray-300 hover:text-white transition-colors">Features</a>
          <a href="#pricing" class="block text-gray-300 hover:text-white transition-colors">Pricing</a>
          <a href="#contact" class="block text-gray-300 hover:text-white transition-colors">Contact</a>
          <router-link to="/login" class="block text-gray-300 hover:text-white transition-colors">Login</router-link>
          <router-link to="/register" class="btn-primary inline-block">Get Started</router-link>
        </div>
      </div>
    </nav>

    <!-- Hero Section -->
    <section class="relative px-6 py-20 lg:py-32">
      <div class="floating-shapes"></div>
      <div class="max-w-7xl mx-auto text-center relative z-10">
        <div class="animate-float mb-8">
          <div class="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-lg border border-white/20 rounded-full px-6 py-3 text-sm">
            <SparklesIcon class="w-4 h-4 text-primary-400" />
            <!-- <span class="text-gray-200">Powered by AI Technology</span> -->
          </div>
        </div>
        
        <h1 class="text-5xl lg:text-7xl font-black mb-6 leading-tight">
          Transform Your
          <span class="text-gradient block">Restaurant Experience</span>
        </h1>
        
        <p class="text-xl lg:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
          Create stunning digital menus accessible via QR codes. Real-time updates, analytics, and AI-powered insights to boost your restaurant's success.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center mb-16">
          <router-link to="/register" class="btn-primary text-lg px-10 py-4">
            Start Free Trial
            <ArrowRightIcon class="w-5 h-5 ml-2 inline" />
          </router-link>
          <button hidden @click="playDemo" class="btn-secondary text-lg px-10 py-4">
            <PlayIcon class="w-5 h-5 mr-2 inline" />
            Watch Demo
          </button>
        </div>
        
        <!-- Hero Image/Demo -->
        <div class="relative max-w-4xl mx-auto">
          <div class="card-glass p-8 animate-glow">
            <div class="bg-gradient-to-br from-dark-700 to-dark-800 rounded-xl p-6 shadow-2xl">
              <div class="flex items-center space-x-2 mb-4">
                <div class="w-3 h-3 bg-red-500 rounded-full"></div>
                <div class="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div class="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
              <div class="bg-dark-900 rounded-lg p-6">

<img src="imgs/landing-emenu.png" alt="Demo Menu" class="w-full h-auto rounded-lg shadow-lg">
                <div class="text-left" hidden>
                  <h3 class="text-2xl font-bold text-gradient mb-4">Bella Vista Restaurant</h3>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-3">
                      <div class="bg-dark-700 rounded-lg p-4">
                        <h4 class="font-semibold text-primary-400">Appetizers</h4>
                        <p class="text-sm text-gray-400">Bruschetta, Calamari, Wings</p>
                      </div>
                      <div class="bg-dark-700 rounded-lg p-4">
                        <h4 class="font-semibold text-secondary-400">Main Courses</h4>
                        <p class="text-sm text-gray-400">Pasta, Steaks, Seafood</p>
                      </div>
                    </div>
                    <div class="space-y-3">
                      <div class="bg-dark-700 rounded-lg p-4">
                        <h4 class="font-semibold text-primary-400">Beverages</h4>
                        <p class="text-sm text-gray-400">Wine, Beer, Cocktails</p>
                      </div>
                      <div class="bg-dark-700 rounded-lg p-4">
                        <h4 class="font-semibold text-secondary-400">Desserts</h4>
                        <p class="text-sm text-gray-400">Tiramisu, Gelato, Cake</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="px-6 py-20 bg-dark-800/50">
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-16">
          <h2 class="text-4xl lg:text-5xl font-bold mb-6">
            Powerful Features for
            <span class="text-gradient">Modern Restaurants</span>
          </h2>
          <p class="text-xl text-gray-300 max-w-3xl mx-auto">
            Everything you need to create, manage, and optimize your digital menu experience
          </p>
        </div>
        
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div v-for="feature in features" :key="feature.title" class="card-glass p-8 hover:scale-105 transition-transform duration-300">
            <div class="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mb-6">
              <component :is="feature.icon" class="w-8 h-8 text-white" />
            </div>
            <h3 class="text-2xl font-bold mb-4">{{ feature.title }}</h3>
            <p class="text-gray-300 leading-relaxed">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="px-6 py-20">
      <div class="max-w-7xl mx-auto">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-8">
          <div v-for="stat in stats" :key="stat.label" class="text-center">
            <div class="text-4xl lg:text-5xl font-black text-gradient mb-2">{{ stat.value }}</div>
            <div class="text-gray-300 font-medium">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Theme Demo Section -->
    <section id="demo" class="px-6 py-20 bg-dark-800/30">
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-16">
          <h2 class="text-4xl lg:text-5xl font-bold mb-6">
            Experience Both
            <span class="text-gradient">Theme Options</span>
          </h2>
          <p class="text-xl text-gray-300 max-w-3xl mx-auto">
            Choose the perfect theme for your restaurant. Our menus automatically adapt to the time of day, or customers can manually switch themes with the toggle button.
          </p>
        </div>
        
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <!-- Dark Theme Demo -->
          <div class="card-glass p-8">
            <div class="bg-gradient-to-br from-dark-700 to-dark-800 rounded-xl p-6 mb-6">
              <h3 class="text-2xl font-bold text-gradient mb-4">Dark Theme</h3>
              <p class="text-gray-300 mb-4">Elegant glassmorphism design with dark gradients - perfect for evening dining</p>
              <div class="bg-dark-900 rounded-lg p-4 mb-4">
                <div class="text-sm text-gray-400">Bella Vista Restaurant</div>
                <div class="text-lg font-semibold text-primary-400">Margherita Pizza - $18.99</div>
                <div class="text-xs text-gray-500">Fresh mozzarella, basil, tomato sauce</div>
              </div>
            </div>
            <a 
              href="/public/menu/demo" 
              target="_blank"
              class="btn-primary w-full text-center block"
            >
              View Dark Theme Demo
            </a>
          </div>
          
          <!-- Light Theme Demo -->
          <div class="card-glass p-8">
            <div class="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl p-6 mb-6 text-gray-800">
              <h3 class="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">Smart Time-Based Themes</h3>
              <p class="text-gray-600 mb-4">Automatically switches to light theme during day (6 AM - 6 PM) and dark theme at night</p>
              <div class="bg-white rounded-lg p-4 mb-4 shadow-md border border-gray-200">
                <div class="text-sm text-gray-500">🕐 Auto-switching based on time</div>
                <div class="text-lg font-semibold text-blue-600">Time: {{ getCurrentTime() }}</div>
                <div class="text-xs text-gray-600">Current: {{ getCurrentTheme() }} Theme</div>
              </div>
            </div>
            <a 
              href="/public/menu/demo" 
              target="_blank"
              class="btn-secondary w-full text-center block"
            >
              Try Smart Menu Demo
            </a>
          </div>
        </div>
        
        <div class="text-center mt-12">
          <p class="text-gray-300 text-lg">
            Smart theme selection with manual toggle options. Customers can always override automatic selection with the theme toggle button.
          </p>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="px-6 py-20 bg-gradient-to-r from-primary-500/20 to-secondary-500/20">
      <div class="max-w-4xl mx-auto text-center">
        <h2 class="text-4xl lg:text-5xl font-bold mb-6">
          Ready to Transform Your Restaurant?
        </h2>
        <p class="text-xl text-gray-300 mb-10">
          Join thousands of restaurants already using Digital Menu AI to enhance their customer experience.
        </p>
        <router-link to="/register" class="btn-primary text-lg px-12 py-4">
          Start Your Free Trial Today
          <ArrowRightIcon class="w-5 h-5 ml-2 inline" />
        </router-link>
      </div>
    </section>

    <!-- Footer -->
    <footer class="px-6 py-12 bg-dark-800 border-t border-white/10">
      <div class="max-w-7xl mx-auto">
        <div class="flex flex-col lg:flex-row justify-between items-center">
          <div class="flex items-center space-x-2 mb-6 lg:mb-0">
            <div class="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
              <QrCodeIcon class="w-6 h-6 text-white" />
            </div>
            <span class="text-xl font-bold text-gradient">Digital Menu AI</span>
          </div>
          <div class="flex space-x-8 text-gray-400">
            <a href="#" class="hover:text-white transition-colors">Privacy</a>
            <a href="#" class="hover:text-white transition-colors">Terms</a>
            <a href="#" class="hover:text-white transition-colors">Support</a>
          </div>
        </div>
        <div class="border-t border-white/10 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; 2025 Digital Menu AI. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { 
  QrCodeIcon, 
  MenuIcon, 
  SparklesIcon, 
  ArrowRightIcon, 
  PlayIcon,
  SmartphoneIcon,
  BarChart3Icon,
  ZapIcon,
  ShieldCheckIcon,
  UsersIcon,
  TrendingUpIcon
} from 'lucide-vue-next'
import { useToast } from '@/composables/useToast'

export default {
  name: 'Home',
  components: {
    QrCodeIcon,
    MenuIcon,
    SparklesIcon,
    ArrowRightIcon,
    PlayIcon,
    SmartphoneIcon,
    BarChart3Icon,
    ZapIcon,
    ShieldCheckIcon,
    UsersIcon,
    TrendingUpIcon
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      showMobileMenu: false,
      features: [
        {
          icon: 'QrCodeIcon',
          title: 'QR Code Menus',
          description: 'Instantly accessible digital menus via QR codes. No app downloads required for your customers.'
        },
        {
          icon: 'ZapIcon',
          title: 'Real-Time Updates',
          description: 'Update menu items, prices, and availability instantly across all locations.'
        },
        {
          icon: 'BarChart3Icon',
          title: 'Analytics & Insights',
          description: 'Track customer preferences, popular items, and optimize your menu performance.'
        },
        {
          icon: 'SmartphoneIcon',
          title: 'Mobile Optimized',
          description: 'Beautiful, responsive design that works perfectly on all mobile devices.'
        },
        {
          icon: 'UsersIcon',
          title: 'Multi-Location',
          description: 'Manage multiple restaurant locations from a single, unified dashboard.'
        },
        {
          icon: 'ShieldCheckIcon',
          title: 'Secure & Reliable',
          description: 'Enterprise-grade security with 99.9% uptime guarantee for your business.'
        }
      ],
      stats: [
        { value: '10K+', label: 'Restaurants' },
        { value: '2M+', label: 'QR Scans' },
        { value: '99.9%', label: 'Uptime' },
        { value: '24/7', label: 'Support' }
      ]
    }
  },
  methods: {
    toggleMobileMenu() {
      this.showMobileMenu = !this.showMobileMenu
    },
    playDemo() {
      // Add demo video logic here
      this.toast.info('Demo Video', 'Demo video functionality will be available soon!')
    },
    getCurrentTime() {
      const now = new Date()
      return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    getCurrentTheme() {
      const currentHour = new Date().getHours()
      return (currentHour >= 6 && currentHour < 18) ? 'Light' : 'Dark'
    },
    getThemeDescription() {
      const currentHour = new Date().getHours()
      if (currentHour >= 6 && currentHour < 18) {
        return 'Daytime hours - Light theme for better readability'
      } else {
        return 'Nighttime hours - Dark theme for comfortable viewing'
      }
    }
  },
  mounted() {
    // Update time every minute
    setInterval(() => {
      this.$forceUpdate()
    }, 60000)
  }
}
</script>
